import { Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Icon, Link } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { FaBalanceScale, FaMapMarkerAlt, FaUsers, FaBriefcase } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { aboutText, contactInfo } from '../lib/storage';

interface StatCardProps {
  number: string;
  description: string;
  icon: IconType;
  delay: number;
  isVisible: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ number, description, icon, delay, isVisible }) => {
  return (
    <Box
      bg="bone"
      borderRadius="8px"
      p={8}
      transition="all 0.8s ease-out"
      opacity={isVisible ? 1 : 0}
      transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
      style={{ transitionDelay: `${delay}s` }}
      borderLeft="4px solid"
      borderColor="stormyTeal"
      _hover={{
        bg: 'paleOak',
        transform: 'translateX(8px)',
      }}
    >
      <HStack spacing={6} align="flex-start">
        <Icon
          as={icon}
          boxSize={{ base: '40px', md: '48px' }}
          color="stormyTeal"
          flexShrink={0}
        />
        <VStack align="flex-start" spacing={2}>
          <Heading
            as="h3"
            fontSize={{ base: '3xl', md: '4xl' }}
            fontWeight={700}
            color="yaleBlue"
            lineHeight={1}
          >
            {number}
          </Heading>
          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            color="graphite"
            fontWeight={500}
            lineHeight="1.5"
          >
            {description}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const stats = [
    {
      number: '15+',
      description: 'Anos de experiência em assessoria jurídica',
      icon: FaBalanceScale,
    },
    {
      number: '8',
      description: 'Unidades e polos de atendimento no Brasil',
      icon: FaMapMarkerAlt,
    },
    {
      number: '12',
      description: 'Profissionais especializados',
      icon: FaUsers,
    },
    {
      number: '7',
      description: 'Áreas de atuação do direito',
      icon: FaBriefcase,
    },
  ];

  return (
    <Box
      id="about"
      ref={sectionRef}
      bg="white"
      py={{ base: 16, md: 24 }}
      position="relative"
    >
      <Container maxW="1400px">
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacing={{ base: 12, lg: 16 }}
          alignItems="center"
        >
          <VStack
            align="flex-start"
            spacing={8}
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateX(0)' : 'translateX(-40px)'}
            transition="all 0.8s ease-out"
          >
            <VStack align="flex-start" spacing={4}>
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight={700}
                color="stormyTeal"
                letterSpacing="2px"
                textTransform="uppercase"
              >
                Sobre Nós
              </Text>
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                color="yaleBlue"
                fontWeight={700}
                lineHeight={1.2}
              >
                Sobre o escritório
              </Heading>
            </VStack>

            <VStack align="flex-start" spacing={4}>
              {aboutText.split('\n\n').map((paragraph) => (
                <Text
                  key={paragraph}
                  fontSize={{ base: 'md', md: 'lg' }}
                  color="graphite"
                  lineHeight="1.8"
                >
                  {paragraph}
                </Text>
              ))}
            </VStack>

            <Box
              as="button"
              bg="yaleBlue"
              color="white"
              px={8}
              py={4}
              borderRadius="4px"
              fontWeight={600}
              fontSize="md"
              transition="all 0.3s ease"
              _hover={{
                bg: 'taupe',
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
              }}
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  const yOffset = -80;
                  const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                  window.scrollTo({ top: y, behavior: 'smooth' });
                }
              }}
            >
              <Link
                href={contactInfo.link}
                _hover={{ textDecoration: 'none', color: 'white' }}
              >
                Fale com o escritório
              </Link>
            </Box>
          </VStack>

          <VStack spacing={6} align="stretch">
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                number={stat.number}
                description={stat.description}
                icon={stat.icon}
                delay={0.2 + index * 0.1}
                isVisible={isVisible}
              />
            ))}
          </VStack>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default About;

